import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingRegion } from '../contexts/PricingRegionContext';
import {
  CONTACT_CONFIG,
  getInstagramLink,
  getWhatsAppLink,
} from '../config/contact';
import { Instagram, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { submitLead } from '../services/api';
import { buildContactWhatsAppMessage } from '../utils/contactMessage';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../utils/analytics';

const ContactCTA = ({ headingLevel = 'h2' }) => {
  const { t, isRTL, currentLang } = useLanguage();
  const { pricingRegion } = usePricingRegion();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const HeadingTag = headingLevel;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFieldFocus = () => {
    if (hasTrackedStart) return;
    setHasTrackedStart(true);
    trackAnalyticsEvent(ANALYTICS_EVENTS.CONTACT_STARTED, {
      ctaLocation: 'contact_form',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackAnalyticsEvent(ANALYTICS_EVENTS.CONTACT_SUBMITTED, {
      ctaLocation: 'contact_form',
    });

    const message = buildContactWhatsAppMessage({ t, formData, pricingRegion });

    window.open(
      getWhatsAppLink(message),
      '_blank',
      'noopener,noreferrer'
    );

    try {
      await submitLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        business: formData.business,
        message: formData.message || null,
        whatsapp_line: 'primary',
        language: currentLang,
        pricing_region: pricingRegion.id,
        currency_code: pricingRegion.currencyCode,
      });
    } catch (error) {
      console.error('Lead could not be saved:', error);
      toast.warning(t('form.saveWarning'));
    } finally {
      setIsSubmitting(false);
    }
    toast.success(t('form.opening'));

    setFormData({
      name: '',
      email: '',
      phone: '',
      business: '',
      message: '',
    });
  };

  return (
    <section
      id="contact"
      className="relative bg-black py-24 md:py-32"
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      {/* Glow Effects */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-[#00FFD1]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-[#00FFD1]/3 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - CTA Content */}
          <div className="space-y-8">
            <HeadingTag className="text-4xl md:text-5xl lg:text-[56px] font-semibold text-white leading-[1.1] tracking-[-0.02em]">
              {t('cta.title')}
            </HeadingTag>
            <p className="text-white/60 text-xl leading-relaxed">
              {t('cta.description')}
            </p>
            <p
              className={`max-w-xl text-base leading-relaxed text-white/48 ${
                isRTL
                  ? 'border-r border-[#00FFD1]/45 pr-4'
                  : 'border-l border-[#00FFD1]/45 pl-4'
              }`}
            >
              {t('cta.subtext')}
            </p>

            {/* WhatsApp */}
            <div className="space-y-4 pt-4">
              <p className="text-white/40 text-sm">{t('form.whatsappChoice')}</p>
              <a
                href={getWhatsAppLink(t('form.contactIntro'))}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-glass-primary"
              >
                <MessageCircle size={20} />
                {CONTACT_CONFIG.whatsappDisplay}
              </a>
            </div>

            {/* Email */}
            <div className="pt-4">
              <p className="text-white/40 text-sm mb-2">{t('form.emailLabel')}</p>
              <a
                href={`mailto:${CONTACT_CONFIG.supportEmail}`}
                className="text-[#00FFD1] text-lg hover:underline"
              >
                {CONTACT_CONFIG.supportEmail}
              </a>
            </div>

            <div className="pt-2">
              <a
                href={getInstagramLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white/70 hover:text-[#00FFD1] transition-colors"
              >
                <Instagram size={18} />
                {CONTACT_CONFIG.instagramDisplay}
              </a>
            </div>
          </div>

          {/* Right - Form */}
          <div className="glass-card p-8 md:p-10" style={{ borderRadius: '16px' }}>
            <h3 className="text-2xl font-semibold text-white mb-6">
              {t('cta.button')}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={handleFieldFocus}
                  placeholder={t('form.name')}
                  required
                  className="glass-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={handleFieldFocus}
                  placeholder={t('form.email')}
                  required
                  className="glass-input"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={handleFieldFocus}
                  placeholder={t('form.phone')}
                  className="glass-input"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="business"
                  value={formData.business}
                  onChange={handleChange}
                  onFocus={handleFieldFocus}
                  placeholder={t('form.business')}
                  required
                  className="glass-input"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={handleFieldFocus}
                  placeholder={t('form.message')}
                  rows={4}
                  className="glass-input resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-glass-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={20} />
                    {t('form.submit')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;
